import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  IconButton,
  Button,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import moment from "moment";

import { patientService } from "@/services";
import { Patient } from "@/resources/types";
import { cn } from "@/lib/utils";
import { scrollable } from "@/components/scrollable";
import { Text } from "@imminently/react/ui/core/text";

interface ExistingDelegateSearchOverlayProps {
  phone: string;
  onSelectDelegate: (person: Patient) => void;
  onClose: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

const ExistingDelegateSearchOverlay: React.FC<ExistingDelegateSearchOverlayProps> = ({
  phone,
  onSelectDelegate,
  onClose,
  onLoadingChange,
}) => {
  const onLoadingChangeRef = React.useRef(onLoadingChange);
  const [isVisible, setIsVisible] = useState(false);
  const [debouncedPhone, setDebouncedPhone] = useState("");
  const [debouncedEnabled, setDebouncedEnabled] = useState(false);
  // Track if user manually closed the overlay and the phone value at that time
  const [dismissedPhone, setDismissedPhone] = useState<string | null>(null);

  /**
   * Track whether we've completed a query for the current debounced parameters.
   *
   * This flag prevents a flicker issue caused by React Query's caching behavior:
   * - React Query returns cached results from previous searches immediately
   * - When user types, we debounce (400ms) before updating search params
   * - During this debounce, cached results from OLD search params can briefly appear
   * - This causes the overlay to flash with stale/incorrect results
   *
   * Solution: Only show the overlay after we've confirmed the fetch completed
   * for the CURRENT search parameters, not stale cached data.
   */
  const [hasQueriedForCurrentParams, setHasQueriedForCurrentParams] = useState(false);

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  // Debounce the search to avoid excessive API calls
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;

    // Validate phone number using normalizePhoneNumber logic
    // If normalizePhoneNumber would succeed, enable the search
    const phoneTrimmed = phone?.trim() || "";
    let phoneNormalized = "";
    let enabled = false;

    if (phoneTrimmed) {
      try {
        // Try to parse the phone number - if it throws or returns null, it's invalid
        const phoneNumber = parsePhoneNumberFromString(phoneTrimmed);
        if (phoneNumber && isValidPhoneNumber(phoneNumber.number)) {
          // Use the normalized format from libphonenumber-js
          phoneNormalized = phoneNumber.number;
          enabled = true;
        } else {
          enabled = false;
        }
      } catch (e) {
        // normalizePhoneNumber would throw, so this is invalid - don't enable search
        enabled = false;
      }
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      setDebouncedPhone(phoneNormalized); // Use normalized phone
      setDebouncedEnabled(enabled);
    }, 400);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [phone]);

  const { data, isLoading, isFetching } = patientService.useGetList(
    debouncedEnabled ? {
      meta: {
        phone: debouncedPhone,
        sort: {
          field: "lastModified",
          order: "desc",
        },
      },
    } as any : null
  );

  const people = data?.data ?? [];

  // Track when we've actually queried for the current parameters
  useEffect(() => {
    if (debouncedEnabled && debouncedPhone) {
      // Reset the flag when parameters change
      setHasQueriedForCurrentParams(false);
    }
  }, [debouncedEnabled, debouncedPhone]);

  // Mark as queried when fetch completes
  useEffect(() => {
    if (!isFetching && debouncedEnabled && debouncedPhone) {
      setHasQueriedForCurrentParams(true);
    }
  }, [isFetching, debouncedEnabled, debouncedPhone, people.length]);

  // Notify parent about loading state changes
  useEffect(() => {
    const phoneTrimmed = phone?.trim() || "";
    let hasSearchConditions = false;

    if (phoneTrimmed) {
      try {
        const phoneNumber = parsePhoneNumberFromString(phoneTrimmed);
        hasSearchConditions = phoneNumber !== undefined && phoneNumber !== null && isValidPhoneNumber(phoneNumber.number);
      } catch (e) {
        hasSearchConditions = false;
      }
    }

    onLoadingChangeRef.current?.(hasSearchConditions && (isLoading || isFetching));
  }, [phone, isLoading, isFetching]);

  useEffect(() => {
    return () => {
      onLoadingChangeRef.current?.(false);
    };
  }, []);

  // Handle manual close - track the phone value when dismissed
  const handleClose = () => {
    onLoadingChangeRef.current?.(false);
    setDismissedPhone(phone?.trim() || "");
    setIsVisible(false);
    onClose();
  };

  // Helper to check if phone is valid
  const checkPhoneValid = (phoneValue: string): boolean => {
    if (!phoneValue) return false;
    try {
      const phoneNumber = parsePhoneNumberFromString(phoneValue);
      return phoneNumber !== undefined && phoneNumber !== null && isValidPhoneNumber(phoneNumber.number);
    } catch (e) {
      return false;
    }
  };

  // Clear dismissal when phone value changes
  useEffect(() => {
    const phoneTrimmed = phone?.trim() || "";
    if (dismissedPhone !== null && dismissedPhone !== phoneTrimmed) {
      setDismissedPhone(null);
    }
  }, [phone, dismissedPhone]);

  // Control visibility - only show when there are results (not during loading)
  // Don't show if user dismissed it for the current phone value
  useEffect(() => {
    const phoneTrimmed = phone?.trim() || "";
    const hasSearchConditions = checkPhoneValid(phoneTrimmed);
    const wasDismissedForThisPhone = dismissedPhone !== null && dismissedPhone === phoneTrimmed;

    // Only show overlay if we have search conditions AND results (not loading) AND we've queried for current params
    // AND the user hasn't dismissed it for this phone value
    const shouldShow = hasSearchConditions && !isLoading && !isFetching && people.length > 0 && hasQueriedForCurrentParams && !wasDismissedForThisPhone;

    setIsVisible(shouldShow);
  }, [phone, isLoading, isFetching, people.length, debouncedPhone, hasQueriedForCurrentParams, dismissedPhone]);

  // Don't render anything if not visible
  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 1000,
        maxHeight: "400px",
        overflow: "hidden",
        mt: 0.5,
        // Remove fixed width and negative margins
        // minWidth: "500px",
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 2,
      }}
    >
      <Stack sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Text variant="subtitle2" sx={{ fontWeight: 600 }}>Existing people</Text>
          <IconButton size="small" onClick={handleClose} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            maxHeight: "320px",
            overflowY: "auto",
          }}
          className={cn(scrollable)}
        >
          <Stack spacing={0.5}>
            {people.map((person: Patient, index: number) => (
              <Box
                key={person.id}
                sx={{
                  p: 1.5,
                  backgroundColor: index % 2 === 0 ? "grey.50" : "background.paper",
                  borderRadius: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack spacing={1}>
                  {/* Person Name Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {person.firstName} {person.lastName}
                      </Typography>
                      {person.phone ? (
                        <Typography variant="caption" color="text.secondary">
                          Phone: {person.phone}
                        </Typography>
                      ) : null}
                      {person.email ? (
                        <Typography variant="caption" color="text.secondary">
                          Email: {person.email}
                        </Typography>
                      ) : null}
                      {person.dateOfBirth && moment(person.dateOfBirth).isValid() ? (
                        <Typography variant="caption" color="text.secondary">
                          DOB: {moment(person.dateOfBirth).format("DD/MM/YYYY")}
                        </Typography>
                      ) : null}
                    </Stack>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onSelectDelegate(person)}
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        px: 1.5,
                        py: 0.5
                      }}
                    >
                      Select
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default ExistingDelegateSearchOverlay;
